import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { Observable } from "rxjs";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableComponent } from "../data-table/data-table.component";
import { QuestionBase } from "../dynamic-form/question-base";
import { NotificationService } from "../notification/notification.service";
import { finalize } from "rxjs/operators";
import { DynamicFormComponent } from "../dynamic-form/dynamic-form.component";

export interface QuickEditModalComponentProps<T extends { id: string }> {
  entity: T;
  /** Used to display the entity name on the title, messages, an delete button */
  entityName: string;
  /** Used to update the table row */
  table?: DataTableComponent<T>;
  /** Used to create the form */
  questions: QuestionBase<string | number | boolean>[];
  update: {
    action: (entity: T) => Observable<T>;
    isAllowed?: boolean;
  };
  delete?: {
    action: (entity: T) => void;
    isAllowed?: boolean;
  };
}

@Component({
  selector: "app-quick-edit-modal",
  templateUrl: "./quick-edit-modal.component.html",
  styleUrls: ["./quick-edit-modal.component.scss"],
})
export class QuickEditModalComponent<T extends { id: string }> implements OnInit, OnDestroy {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;

  loading: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: QuickEditModalComponentProps<T>,
    public dialog: MatDialog,
    private notificationService: NotificationService,
    private loader: LoadingService,
  ) {}

  // #region Hooks

  ngOnInit(): void {
    // do something
  }

  ngOnDestroy(): void {
    // do something
  }

  // #endregion

  // #region Handlers

  onChange(_event: Event): void {
    // do something
  }

  onSubmit(entity: T): void {
    this.updateEntity(entity);
  }

  onDelete(): void {
    this.deleteEntity();
  }

  // #endregion

  // #region Functions

  canEdit(): boolean {
    return this.data.update.isAllowed === undefined || this.data.update?.isAllowed;
  }

  canDelete(): boolean {
    return !!this.data.delete?.isAllowed;
  }

  private updateEntity(entity: T): void {
    this.data.update
      .action(entity)
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (updatedEntity: T) => {
          if (this.data.table) {
            this.updateTable(updatedEntity);
          }
          this.dialog.closeAll();
          this.notificationService.success(`${this.data.entityName} updated`);
        },
        (error) => this.notificationService.error(error || `Could not update ${this.data.entityName}`),
      )
      .add(() => {
        this.loading = false;
        this.loader.hide();
      });
  }

  private deleteEntity(): void {
    this.data.delete?.action(this.data.entity);
  }

  private updateTable(updatedEntity: T): void {
    this.data.table.config.rows = this.data.table.config.rows.map((entity) =>
      entity.id === this.data.entity.id ? { ...this.data.entity, ...updatedEntity } : entity,
    );
  }

  // #endregion
}
